import type {
  MarketingConsentAnalyticsResponse,
  MarketingConsentLeadsResponse,
} from "../../pages/Campaigns/components/MarketingConsent/types/marketingConsent.types";
import { baseApi } from "./baseApi";

export const campaignApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCampaigns: builder.query({
      query: ({
        search_query,
        skip,
        limit,
        category,
        campaign_status,
        created_by,
        use_case,
        branch,
        started_on_from,
        started_on_to,
        created_on_from,
        created_on_to,
      }: {
        search_query: string;
        skip: number;
        limit: number;
        category?: string | null;
        campaign_status?: string[] | null;
        created_by?: string | null;
        use_case?: string | null;
        branch?: string | null;
        started_on_from?: string | null;
        started_on_to?: string | null;
        created_on_from?: string | null;
        created_on_to?: string | null;
      }) => ({
        url: `/api/v1/campaigns/list`,
        method: "GET",
        params: {
          search_query: search_query ?? "",
          skip,
          limit,
          ...(category && { category }),
          ...(campaign_status?.length &&
            campaign_status.length > 0 && { status: campaign_status }),
          ...(created_by && { created_by }),
          ...(use_case && { use_case }),
          ...(branch && { branch }),
          ...(started_on_from && { started_on_from }),
          ...(started_on_to && { started_on_to }),
          ...(created_on_from && { created_on_from }),
          ...(created_on_to && { created_on_to }),
        },
      }),
      providesTags: [{ type: "Campaigns", id: "CampaignsList" }],
    }),

    getContacts: builder.query({
      query: (filters: { query: string; skip: number; limit: number }) => ({
        url: `/contacts/?query=${filters.query ?? ""}&skip=${
          filters.skip
        }&limit=${filters.limit}`,
        method: "GET",
      }),
      providesTags: [{ type: "Contacts", id: "ContactsList" }],
    }),

    uploadContactsCsv: builder.mutation({
      query: (payload: FormData) => ({
        url: `/contacts/upload`,
        method: "POST",
        body: payload,
        formData: true,
      }),
      invalidatesTags: [{ type: "Contacts", id: "ContactsList" }],
    }),

    getCampaignLogs: builder.query({
      query: ({
        skip,
        limit,
        campaign_id,
        user_id,
      }: {
        skip?: number;
        limit?: number;
        campaign_id?: string;
        user_id?: string;
      }) => ({
        url: `/api/v1/campaign-logs`,
        method: "GET",
        params: { skip, limit, campaign_id, user_id },
      }),
      providesTags: ["CampaignLogs"],
    }),

    getCampaignTemplatesByType: builder.query({
      query: (params: {
        templateType: string;
        started_on_from?: string | null;
        started_on_to?: string | null;
        created_on_from?: string | null;
        created_on_to?: string | null;
        use_case?: string | null;
        status?: string | null;
        created_by?: string | null;
        branch?: string | null;
        category?: string | null;
        skip?: number;
        limit?: number;
        query?: string;
        is_favorite?: boolean;
      }) => {
        const queryParams: string[] = [];
        let searchQuery: string = "";
        let url = "/campaign-template/templates";

        Object.entries(params).forEach(([key, value]) => {
          if (
            key !== "templateType" &&
            value !== null &&
            value !== "" &&
            value !== undefined
          ) {
            queryParams?.push(`${key}=${value}`);
          }
        });
        searchQuery = queryParams?.join("&");
        url =
          url +
          (params?.templateType ? `/${params?.templateType}` : "") +
          `?${searchQuery}`;

        return {
          url,
          method: "GET",
        };
      },
      providesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),

    getAllCampaignCategories: builder.query({
      query: () => ({
        url: `/api/v1/campaigns/usecases/categories`,
        method: "GET",
      }),
      providesTags: ["CampaignCategories"],
    }),

    getAllCampaignUseCases: builder.query({
      query: ({ category }: { category?: string } = {}) => ({
        url: `/api/v1/campaigns/usecases/list`,
        method: "GET",
        params: category ? { category_key: category } : undefined,
      }),
      providesTags: ["CampaignUseCases"],
    }),

    getAllCampaignBranches: builder.query({
      query: ({
        useCase,
        category,
      }: {
        useCase?: string;
        category?: string;
      } = {}) => ({
        url: `/api/v1/campaigns/usecases/branches`,
        method: "GET",
        params: {
          ...(useCase && { use_case_key: useCase }),
          ...(category && { category_key: category }),
        },
      }),
      providesTags: ["CampaignBranches"],
    }),

    getSampleCSV: builder.query({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/download-sample-csv/${campaignId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const contentDisposition = response.headers.get(
            "Content-Disposition"
          );
          let filename = "sample-contacts.csv";

          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename=(.+?)(?:;|$)/
            );
            if (filenameMatch) {
              filename = filenameMatch[1].replace(/"/g, "");
            }
          }

          return { blob, filename };
        },
      }),
      keepUnusedDataFor: 0,
    }),

    deleteContactCSV: builder.mutation({
      query: (id: string) => ({
        url: `/contacts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Contacts", id: "ContactsList" }],
    }),

    getVoices: builder.query({
      query: () => `/client_config/voice_agents`,
    }),

    composeCallScript: builder.mutation({
      query: (payload: { use_case_key: string; user_content: string }) => ({
        url: `/campaign-template/compose-call-script`,
        method: "POST",
        body: payload,
      }),
    }),

    composeScriptWithAI: builder.mutation({
      query: (payload: {
        category: string;
        use_case: string;
        channel: string;
        branch?: string;
        user_content: string;
      }) => ({
        url: `/campaign-template/compose-script-with-ai`,
        method: "POST",
        body: payload,
      }),
    }),

    getCampaignTemplateById: builder.query({
      query: (id: string) => ({
        url: `/campaign-template/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "CampaignTemplates", id }],
    }),

    deleteCampaignTemplate: builder.mutation({
      query: (id: string) => ({
        url: `/campaign-template/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),

    createTemplate: builder.mutation({
      query: (payload: object) => ({
        url: `/campaign-template/`,
        method: "POST",
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),

    createCampaign: builder.mutation({
      query: (payload: Record<string, unknown>) => ({
        url: "/api/v1/campaigns/create",
        method: "POST",
        body: payload,
      }),
    }),

    updateCampaign: builder.mutation({
      query: (payload: {
        campaignId: string;
        name?: string;
        use_case?: string;
        branch?: string;
        category?: string;
        timezone?: string;
        audience_file_key?: string;
        segment_id?: string;
        campaign_ai_type?: string;
      }) => {
        const { campaignId, ...body } = payload;
        return {
          url: `/api/v1/campaigns/update/${campaignId}`,
          method: "PATCH",
          body,
        };
      },
    }),

    getCampaignById: builder.query({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/${campaignId}`,
        method: "GET",
      }),
      providesTags: (result, error, campaignId) => [
        { type: "Campaigns", id: campaignId },
      ],
      keepUnusedDataFor: 0,
    }),

    addCampaignReachOut: builder.mutation({
      query: (payload: {
        campaignId: string;
        channel: string;
        template_id: string;
        run_time: string;
        sender_email?: string;
        sender_name?: string;
        campaign_ai_type?: string;
      }) => {
        const { campaignId, ...body } = payload;
        return {
          url: `/api/v1/campaigns/add-campaign-reachout/${campaignId}`,
          method: "POST",
          body,
        };
      },
    }),

    updateTemplate: builder.mutation({
      query: ({ id, data }: { id: string; data: object }) => ({
        url: `/campaign-template/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),

    editCampaignReachOut: builder.mutation({
      query: (payload: {
        campaignId: string;
        reachoutId: string;
        template_id?: string;
        run_time?: string;
        sender_email?: string;
        sender_name?: string;
        campaign_ai_type?: string;
      }) => {
        const { campaignId, reachoutId, ...body } = payload;
        return {
          url: `/api/v1/campaigns/update-campaign-reachout/${campaignId}/${reachoutId}`,
          method: "PATCH",
          body,
        };
      },
    }),

    removeCampaignReachOut: builder.mutation({
      query: ({
        campaignId,
        reachoutId,
      }: {
        campaignId: string;
        reachoutId: string;
      }) => ({
        url: `/api/v1/campaigns/remove-campaign-reachout/${campaignId}/${reachoutId}`,
        method: "DELETE",
      }),
    }),

    launchCampaign: builder.mutation({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/launch/${campaignId}`,
        method: "POST",
      }),
    }),

    deleteCampaign: builder.mutation({
      query: (id: string) => ({
        url: `/api/v1/campaigns/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Campaigns", id: "CampaignsList" }],
    }),

    abortCampaign: builder.mutation({
      query: (id: string) => ({
        url: `/api/v1/campaigns/${id}/terminate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Campaigns", id: "CampaignsList" },
        { type: "Campaigns", id },
      ],
    }),
    getBulkProgress: builder.query<
      {
        progress_mapping: Record<
          string,
          {
            completed_count: number;
            total_count: number;
            completed_percentage: number;
            total_modalities: number;
          }
        >;
      },
      string[]
    >({
      query: (ids) => ({
        url: "/api/v1/campaigns/progress",
        method: "POST",
        body: { campaign_ids: ids },
      }),
    }),

    getAllUsers: builder.query({
      query: () => ({
        url: "/users/",
        method: "GET",
      }),
      providesTags: [
        {
          type: "User",
          id: "ALL",
        },
      ],
    }),

    downloadContactCSV: builder.mutation({
      query: ({ fileId, fileName }: { fileId: string; fileName: string }) => ({
        url: `/contacts/download-csv/${fileId}`,
        method: "GET",
        responseHandler: async (response) => {
          if (!response.ok) {
            throw new Error("Failed to download contact CSV");
          }

          const csvContent = await response.text();
          const csvBlob = new Blob([csvContent], { type: "text/csv" });
          const csvUrl = URL.createObjectURL(csvBlob);

          const a = document.createElement("a");
          a.href = csvUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(csvUrl);

          return { success: true };
        },
      }),
    }),

    sendTestCall: builder.mutation({
      query: (payload: {
        template_id: string;
        replacement_vars: Record<string, string>;
        phone_number: string;
      }) => ({
        url: `/campaign-template/test-call`,
        method: "POST",
        body: payload,
      }),
    }),

    sendTestEmail: builder.mutation({
      query: (payload: {
        template_id: string;
        replacement_vars: Record<string, string>;
        email: string;
        sender_email?: string;
      }) => ({
        url: `/campaign-template/test-email`,
        method: "POST",
        body: payload,
      }),
    }),

    sendTestSms: builder.mutation({
      query: (payload: {
        template_id: string;
        replacement_vars: Record<string, string>;
        phone_number: string;
      }) => ({
        url: `/campaign-template/test-sms`,
        method: "POST",
        body: payload,
      }),
    }),

    getCampaignLogById: builder.query({
      query: (payload: {
        skip?: number;
        limit?: number;
        campaign_id?: string;
        campaign_name?: string;
        user_name?: string;
        user_id?: string;
      }) => {
        const queryParams: string[] = [];
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== "" && value !== undefined) {
            queryParams?.push(`${key}=${encodeURIComponent(value)}`);
          }
        });
        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return {
          url: `/api/v1/campaign-logs${queryString}`,
          method: "GET",
        };
      },
    }),

    duplicateCampaign: builder.mutation({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/duplicate/${campaignId}`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "Campaigns", id: "CampaignsList" }],
    }),

    getCampaignReport: builder.query({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/${campaignId}/campaign-summary`,
        method: "GET",
      }),
    }),

    getCampaignActivityLogs: builder.query({
      query: (payload: {
        campaign_id: string;
        skip?: number;
        limit?: number;
        execution_log_status?: string;
        channel?: string;
        start_date?: string;
        end_date?: string;
      }) => {
        const queryParams: string[] = [];
        Object.entries(payload).forEach(([key, value]) => {
          if (value !== null && value !== "" && value !== undefined) {
            queryParams?.push(`${key}=${encodeURIComponent(value)}`);
          }
        });
        const queryString =
          queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

        return {
          url: `/api/v1/campaign-logs/activity-logs${queryString}`,
          method: "GET",
        };
      },
    }),
    getCampaignFilters: builder.mutation({
      query: (payload: { channel: string }) => ({
        url: `dashboard/leads/chatbot/campaign-lead-listing/dropdown/${payload.channel}`,
        method: "GET",
      }),
    }),
    getCampaignOutcomeOptions: builder.query({
      query: (params: { use_case: string; channel?: string }) => {
        const baseUrl = `/dashboard/leads/chatbot/campaign-lead-listing/outcome-dropdown/${params?.use_case}`;
        const url = params?.channel
          ? `${baseUrl}?channel=${params.channel}`
          : baseUrl;
        return {
          url,
          method: "GET",
        };
      },
    }),
    getVfCampaignTemplates: builder.query({
      query: () => ({
        url: "/campaign-template/vf-templates",
        method: "GET",
      }),
    }),
    addCampaignTemplateFavorite: builder.mutation({
      query: (templateId: string) => ({
        url: `/campaign-template/${templateId}/favorite`,
        method: "POST",
      }),
      invalidatesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),
    removeCampaignTemplateFavorite: builder.mutation({
      query: (templateId: string) => ({
        url: `/campaign-template/${templateId}/favorite`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "CampaignTemplates", id: "TemplateList" }],
    }),
    retryCampaign: builder.mutation({
      query: ({
        id,
        status,
        channel,
      }: {
        id: string;
        status: string[];
        channel: string;
      }) => ({
        url: `/api/v1/campaigns/retry-campaign/${id}`,
        method: "POST",
        body: { status, channel },
      }),
      invalidatesTags: [{ type: "Campaigns", id: "List" }],
    }),
    getCampaignAggregatedData: builder.query({
      query: ({
        campaign_id,
        channel,
      }: {
        campaign_id: string;
        channel: string;
      }) => ({
        url: `/dashboard/analytics/campaigns/aggregated-metrics/${channel}?campaign_id=${campaign_id}`,
        method: "GET",
      }),
    }),
    getCampaignKeywordSplashAnalytics: builder.query({
      query: ({
        campaign_id,
        channel,
      }: {
        campaign_id: string;
        channel: string;
      }) => ({
        url: `/dashboard/analytics/campaigns/keyword-splash/${channel}?campaign_id=${campaign_id}`,
        method: "GET",
      }),
    }),
    getCampaignModalityOvertTimeData: builder.query({
      query: ({
        campaign_id,
        channel,
        category_dropdown,
        time_dropdown,
        time_zone,
      }: {
        campaign_id: string;
        channel: string;
        category_dropdown: string;
        time_dropdown: string;
        time_zone: string;
      }) => {
        const queryParams: string[] = [];

        queryParams.push(`campaign_id=${campaign_id}`);
        queryParams.push(
          `category_dropdown=${encodeURIComponent(category_dropdown)}`
        );
        queryParams.push(`time_dropdown=${encodeURIComponent(time_dropdown)}`);
        queryParams.push(`timezone=${encodeURIComponent(time_zone)}`);

        const queryString = `?${queryParams.join("&")}`;

        return {
          url: `/dashboard/analytics/campaigns/modality-over-time/${channel}${queryString}`,
          method: "GET",
        };
      },
    }),
    getCampaignRebookingAnalytics: builder.query({
      query: ({
        campaign_id,
        channel,
      }: {
        campaign_id: string;
        channel: string;
      }) => ({
        url: `/dashboard/analytics/campaigns/extraction-metrics/${channel}?campaign_id=${campaign_id}`,
        method: "GET",
      }),
    }),
    getCampaignLocationWiseData: builder.query({
      query: ({
        campaign_id,
        channel,
      }: {
        campaign_id: string;
        channel: string;
      }) => ({
        url: `/dashboard/analytics/campaigns/location-metrics/${channel}?campaign_id=${campaign_id}`,
        method: "GET",
      }),
    }),
    deleteCampaignReachOutAndContacts: builder.mutation({
      query: (campaignId: string) => ({
        url: `/api/v1/campaigns/reachouts/${campaignId}`,
        method: "DELETE",
      }),
    }),
    getCampaignLanguageOptions: builder.query({
      query: (channel: string) => {
        return {
          url: `/api/v1/campaigns/supported-languages/${channel}`,
          method: "GET",
        };
      },
      providesTags: (result, error, channel) => [
        { type: "CampaignLanguages", id: channel },
      ],
    }),

    getTemplateLanguages: builder.query({
      query: () => ({
        url: "/campaign-template/languages",
        method: "GET",
      }),
    }),

    getSegments: builder.query({
      query: (params: {
        page?: number;
        limit?: number;
        segment_type?: string;
        created_by?: string;
        entity?: string;
        sort_key?: string;
        sort_order?: string;
        search?: string;
      }) => ({
        url: `/segments`,
        method: "GET",
        params: {
          ...(params.page && { page: params.page }),
          ...(params.limit && { limit: params.limit }),
          ...(params.segment_type && { segment_type: params.segment_type }),
          ...(params.created_by && { created_by: params.created_by }),
          ...(params.entity && { entity: params.entity }),
          ...(params.sort_key && { sort_key: params.sort_key }),
          ...(params.sort_order && { sort_order: params.sort_order }),
          ...(params.search && { search: params.search }),
        },
      }),
      providesTags: [{ type: "Segments", id: "SegmentsList" }],
    }),

    getSegmentById: builder.query({
      query: (segmentId: string) => ({
        url: `/segments/${segmentId}`,
        method: "GET",
      }),
      providesTags: (result, error, segmentId) => [
        { type: "Segments", id: segmentId },
      ],
    }),

    createSegment: builder.mutation({
      query: (payload: {
        name: string;
        segment_type: string;
        entity: string;
        logical_operator?: "and" | "or";
        filter_groups?: {
          conditions: {
            key: string;
            comparison_operator: string;
            value:
              | string
              | number
              | string[]
              | number[]
              | boolean
              | Record<string, unknown>;
            is_custom_field: boolean;
            object_type?: "lead" | "deal";
          }[];
          logical_operator: string;
        }[];
      }) => ({
        url: `/segments`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Segments", id: "SegmentsList" }],
    }),

    updateSegment: builder.mutation({
      query: ({
        segmentId,
        ...body
      }: {
        segmentId: string;
        name?: string;
        segment_type?: string;
        entity?: string;
        logical_operator?: "and" | "or";
        filter_groups?: {
          conditions: {
            key: string;
            comparison_operator: string;
            value:
              | string
              | number
              | string[]
              | number[]
              | boolean
              | Record<string, unknown>;
            is_custom_field: boolean;
            object_type?: "lead" | "deal";
          }[];
          logical_operator: string;
        }[];
      }) => ({
        url: `/segments/${segmentId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { segmentId }) => [
        { type: "Segments", id: "SegmentsList" },
        { type: "Segments", id: segmentId },
        { type: "Segments", id: `${segmentId}-leads` },
        { type: "Segments", id: `${segmentId}-size` },
      ],
    }),

    getSegmentLeads: builder.query({
      query: ({
        segmentId,
        page,
        limit,
      }: {
        segmentId: string;
        page?: number;
        limit?: number;
      }) => ({
        url: `/segments/${segmentId}/leads`,
        method: "GET",
        params: {
          ...(page && { page }),
          ...(limit && { limit }),
        },
      }),
      providesTags: (result, error, { segmentId }) => [
        { type: "Segments", id: `${segmentId}-leads` },
      ],
    }),

    getMarketingConsentLeads: builder.query<
      MarketingConsentLeadsResponse,
      {
        page: number;
        limit: number;
        search?: string;
        sort_order?: "asc" | "desc";
        consent_filter?: ("yes" | "no" | "unsubscribed")[];
      }
    >({
      query: ({ page, limit, search, sort_order, consent_filter }) => ({
        url: `/marketing-consent/leads`,
        method: "GET",
        params: {
          page,
          limit,
          ...(search && { search }),
          ...(sort_order && { sort_order }),
          ...(consent_filter?.length && { consent_filter }),
        },
      }),
      providesTags: [{ type: "MarketingConsent", id: "LeadsList" }],
    }),

    getMarketingConsentAnalytics: builder.query<
      MarketingConsentAnalyticsResponse,
      void
    >({
      query: () => ({
        url: `/marketing-consent/analytics`,
        method: "GET",
      }),
      providesTags: [{ type: "MarketingConsent", id: "Analytics" }],
    }),

    getSegmentSize: builder.query({
      query: (segmentId: string) => ({
        url: `/segments/${segmentId}/size`,
        method: "GET",
      }),
      providesTags: (result, error, segmentId) => [
        { type: "Segments", id: `${segmentId}-size` },
      ],
    }),

    exportSegment: builder.mutation({
      query: ({
        segmentId,
        format,
        name,
        exclude_fields,
      }: {
        segmentId: string;
        format: "csv" | "xls" | "xlsx";
        name?: string;
        exclude_fields: string[] | null;
      }) => ({
        url: `/segments/${segmentId}/export`,
        method: "POST",
        body: { format, ...(name && { name }), exclude_fields },
        responseHandler: async (response: Response) => {
          const disposition = response.headers.get("Content-Disposition") || "";
          const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
          const filename = filenameMatch?.[1] || `segment_export.${format}`;
          const blob = await response.blob();
          return { blob, filename };
        },
        cache: "no-cache" as const,
      }),
    }),

    linkSegmentToCampaign: builder.mutation({
      query: ({
        segmentId,
        campaign_id,
      }: {
        segmentId: string;
        campaign_id: string;
      }) => ({
        url: `/segments/${segmentId}/campaign-audience`,
        method: "POST",
        body: { campaign_id },
      }),
    }),

    deleteSegment: builder.mutation({
      query: (segmentId: string) => ({
        url: `/segments/${segmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Segments", id: "SegmentsList" }],
    }),

    validateCsvDte: builder.mutation<
      {
        isValid: boolean;
        identifiedColumns: {
          originalHeader: string;
          normalisedHeader: string;
        }[];
        unknownColumns: { originalHeader: string; normalisedHeader: string }[];
        missingRequiredAnyOf: string[];
        duplicateColumns: string[];
      },
      { csvId: string }
    >({
      query: ({ csvId }) => {
        const formData = new FormData();
        formData.append("csv_id", csvId);
        return {
          url: `/contacts/validate-csv-dte`,
          method: "POST",
          body: formData,
          formData: true,
        };
      },
    }),

    uploadCampaignAttachment: builder.mutation({
      query: (payload: { attachment: File }) => {
        const formData = new FormData();
        formData.append("files", payload.attachment);
        formData.append("source", "conversation");
        return {
          url: `/attachments/`,
          method: "POST",
          body: formData,
        };
      },
    }),
  }),

  overrideExisting: true,
});

export const {
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery,
  useGetContactsQuery,
  useGetCampaignLogsQuery,
  useGetCampaignTemplatesByTypeQuery,
  useGetAllCampaignCategoriesQuery,
  useGetAllCampaignUseCasesQuery,
  useGetAllCampaignBranchesQuery,
  useLazyGetSampleCSVQuery,
  useUploadContactsCsvMutation,
  useDeleteContactCSVMutation,
  useGetVoicesQuery,
  useComposeCallScriptMutation,
  useComposeScriptWithAIMutation,
  useGetCampaignTemplateByIdQuery,
  useDeleteCampaignTemplateMutation,
  useCreateTemplateMutation,
  useCreateCampaignMutation,
  useUpdateCampaignMutation,
  useGetCampaignByIdQuery,
  useLazyGetCampaignByIdQuery,
  useAddCampaignReachOutMutation,
  useUpdateTemplateMutation,
  useEditCampaignReachOutMutation,
  useRemoveCampaignReachOutMutation,
  useLaunchCampaignMutation,
  useDeleteCampaignMutation,
  useAbortCampaignMutation,
  useGetBulkProgressQuery,
  useGetAllUsersQuery,
  useDownloadContactCSVMutation,
  useSendTestCallMutation,
  useSendTestEmailMutation,
  useSendTestSmsMutation,
  useGetCampaignLogByIdQuery,
  useDuplicateCampaignMutation,
  useGetCampaignReportQuery,
  useLazyGetCampaignReportQuery,
  useGetCampaignActivityLogsQuery,
  useGetCampaignFiltersMutation,
  useGetCampaignOutcomeOptionsQuery,
  useGetVfCampaignTemplatesQuery,
  useAddCampaignTemplateFavoriteMutation,
  useRemoveCampaignTemplateFavoriteMutation,
  useRetryCampaignMutation,
  useGetCampaignAggregatedDataQuery,
  useGetCampaignKeywordSplashAnalyticsQuery,
  useGetCampaignModalityOvertTimeDataQuery,
  useGetCampaignRebookingAnalyticsQuery,
  useGetCampaignLocationWiseDataQuery,
  useDeleteCampaignReachOutAndContactsMutation,
  useGetCampaignLanguageOptionsQuery,
  useGetTemplateLanguagesQuery,
  useUploadCampaignAttachmentMutation,
  useGetSegmentsQuery,
  useGetSegmentByIdQuery,
  useLazyGetSegmentByIdQuery,
  useGetSegmentLeadsQuery,
  useGetMarketingConsentLeadsQuery,
  useGetMarketingConsentAnalyticsQuery,
  useGetSegmentSizeQuery,
  useCreateSegmentMutation,
  useUpdateSegmentMutation,
  useExportSegmentMutation,
  useLinkSegmentToCampaignMutation,
  useDeleteSegmentMutation,
  useValidateCsvDteMutation,
} = campaignApi;
